import type {
  CaptureHandlers,
  CaptureSource,
} from "@/hooks/voice/captureSource";
import type { PluginListenerHandle } from "@capacitor/core";
import { CohesiveAudioCapture, decodeAudioChunk } from "./audioCapture";

/**
 * CaptureSource backed by the CohesiveAudioCapture plugin (AVAudioEngine with
 * the `audio` background mode), so recording survives screen lock and app
 * switching. Lives here because everything that imports a Capacitor plugin
 * lives in src/native/; the interface it satisfies is platform-agnostic and
 * stays with the engine in hooks/voice/.
 *
 * Imports the interface types only, so there is no runtime cycle with the
 * factory in hooks/voice/captureSource that constructs this class.
 */
export class NativeCaptureSource implements CaptureSource {
  readonly kind = "native" as const;
  private listeners: PluginListenerHandle[] = [];
  private muted = false;

  async start(handlers: CaptureHandlers): Promise<void> {
    try {
      this.listeners.push(
        await CohesiveAudioCapture.addListener("audioChunk", (event) => {
          if (this.muted) return;
          handlers.onChunk({
            pcm: decodeAudioChunk(event.data),
            timestampMs: event.timestampMs,
            epoch: event.epoch,
          });
        }),
      );
      this.listeners.push(
        await CohesiveAudioCapture.addListener("captureState", (event) => {
          if (event.state === "interrupted") {
            handlers.onInterruption({
              phase: "began",
              shouldResume: false,
              reason: event.reason,
            });
          } else if (event.state === "running" && event.shouldResume) {
            // Fires only on interruption recovery, not on the initial start.
            handlers.onInterruption({
              phase: "ended",
              shouldResume: true,
              reason: event.reason,
            });
          }
        }),
      );
      await CohesiveAudioCapture.start();
    } catch (error) {
      // The plugin is a singleton, so listeners left attached by a failed
      // start (denied mic permission being the common case) would stack on
      // the next attempt and deliver every chunk twice.
      await this.stop();
      throw error;
    }
  }

  /**
   * Rebuild the native engine after an interruption the system did not tell
   * us to auto-resume from. iOS tears the engine down for the interruption,
   * so unmuting alone would leave a dead mic behind a "recording" UI.
   */
  async resume(): Promise<void> {
    await CohesiveAudioCapture.resume();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    // Also mute native-side so the plugin's own buffering (if any) and the
    // timeline gap agree with what JS sees.
    void CohesiveAudioCapture.setMuted({ muted }).catch(() => undefined);
  }

  async stop(): Promise<void> {
    for (const listener of this.listeners) {
      void listener.remove().catch(() => undefined);
    }
    this.listeners = [];
    await CohesiveAudioCapture.stop().catch(() => undefined);
  }
}
